import { storeSelector, usePolicyContext } from '@Policy/store';
import { MergedRiskAiFilterRule } from '@Policy/utils/types';
import { useMemo } from 'react';
import {
  CorrelatedRuleShallow,
  RuleType,
} from 'src/pages/GlobalSetting/CorrelatedRuleTemplates/types';

export const useRiskAIRules = (props: {
  rules: CorrelatedRuleShallow[];
  policyRules: MergedRiskAiFilterRule[];
  onRulesUpdate: (rules: MergedRiskAiFilterRule[]) => void;
}) => {
  const { rules, policyRules, onRulesUpdate } = props;
  const contextStore = usePolicyContext();
  const contextSelector = storeSelector(contextStore);
  const { createMergedRiskAiFilterRule } = contextSelector(
    ({ createMergedRiskAiFilterRule }) => ({
      createMergedRiskAiFilterRule,
    })
  );

  const allRules: MergedRiskAiFilterRule[] = useMemo(() => {
    const ruleMap = new Map(policyRules.map((rule) => [rule.raiRuleId, rule]));
    return rules.map((rule) =>
      createMergedRiskAiFilterRule(rule, ruleMap.get(rule.raiRuleId))
    );
  }, [createMergedRiskAiFilterRule, policyRules, rules]);

  const selectedRules: MergedRiskAiFilterRule[] = useMemo(() => {
    const ruleMap = new Map(policyRules.map((rule) => [rule.raiRuleId, rule]));
    return allRules.filter((rule) => ruleMap.get(rule.raiRuleId));
  }, [allRules, policyRules]);

  const { preDefinedRules, selectedPreDefinedRules } = useMemo(() => {
    const preDefinedRules = allRules.filter(
      ({ tmRaiRule }) => tmRaiRule.raiRuleBuiltIn
    );
    const selectedPreDefinedRules = selectedRules.filter(
      ({ tmRaiRule }) => tmRaiRule.raiRuleBuiltIn
    );
    return { preDefinedRules, selectedPreDefinedRules };
  }, [allRules, selectedRules]);

  const { customRules, selectedCustomRules } = useMemo(() => {
    const customRules = allRules.filter(
      ({ tmRaiRule }) => !tmRaiRule.raiRuleBuiltIn
    );
    const selectedCustomRules = selectedRules.filter(
      ({ tmRaiRule }) => !tmRaiRule.raiRuleBuiltIn
    );
    return { customRules, selectedCustomRules };
  }, [allRules, selectedRules]);

  const setSelectedBuiltInRules = (selectedRules: MergedRiskAiFilterRule[]) => {
    const customRules = policyRules.filter(
      (rule) => rule.tmRaiRule?.raiRuleBuiltIn === RuleType.UserDefined
    );
    onRulesUpdate([...customRules, ...selectedRules]);
  };

  const setSelectedCustomRules = (selectedRules: MergedRiskAiFilterRule[]) => {
    const builtInRules = policyRules.filter(
      (rule) => rule.tmRaiRule?.raiRuleBuiltIn === RuleType.PreDefined
    );
    onRulesUpdate([...builtInRules, ...selectedRules]);
  };

  return {
    allRules,
    selectedRules,
    preDefinedRules,
    customRules,
    selectedCustomRules,
    selectedPreDefinedRules,
    setSelectedBuiltInRules,
    setSelectedCustomRules,
  };
};
